﻿/* https://brandplatform.smartphoto.com/general/organisms/main-nav/ */
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	getParentWithClassName,
	hasClass,
	expressQuerySelectorAll,
	expressRemoveClass,
	getSelfOrParentWithClassName,
} from "../../common/html";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { isTouchDevice } from "../../common/device";
import { debounce } from "../../utils/debounce";

export interface ICreateNavigationDesktopDependencies {
	readonly loadDesktopMenuItemAsync: (firstLevel: number, secondLevel: number) => Promise<string>;
}

export function initNavigationDesktop(containerEl: HTMLElement, deps: ICreateNavigationDesktopDependencies) {
	const firstLevelMenuEls = expressQuerySelectorAll<HTMLElement>(
		containerEl,
		".technical-desktop-first-level-menu-item",
	);
	const { loadDesktopMenuItemAsync } = deps;
	const loadedMenuItemsKeys: number[][] = [];
	const disposable: IDisposable = compositeDisposable([]);
	const firstLevelActiveClass = "o-menu-first-level__button--active";
	const secondLevelActiveClass = "o-menu-second-level__list-item--active";
	const keyboardActiveClass = "u-focussed";
	const keyboardFocusClass = "focussed";

	const closeMenu = (e: Event) => {
		e.stopPropagation();
		const clickedItem = e.currentTarget as HTMLElement;
		if (getParentWithClassName(clickedItem, "o-menu-second-level__list")) return;
		firstLevelMenuEls.forEach((el) => closeFirstLevelMenuItemAndResetChildMenus(el));
	};

	const resetFocussedElement = (parentEl: HTMLElement) => {
		const focusEl = expressQuerySelector<HTMLElement>(parentEl, "." + keyboardActiveClass, false);
		if (focusEl) expressRemoveClass(focusEl, keyboardActiveClass);
	};

	const closeFirstLevelMenuItemAndResetChildMenus = (menuItem: HTMLElement) => {
		resetFocussedElement(menuItem);
		const buttonEL = expressQuerySelector<HTMLElement>(menuItem, ".technical-level1-button", true);
		expressRemoveClass(buttonEL, firstLevelActiveClass);
		closeAllSecondLevelMenuItemsAndResetChildMenus(menuItem);
	};

	const closeAllSecondLevelMenuItemsAndResetChildMenus = (menuItem: HTMLElement) => {
		const secondLevelMenuEls = expressQuerySelectorAll<HTMLElement>(menuItem, ".technical-desktop-menu-second");
		secondLevelMenuEls.forEach((secondLevelMenuEl) => {
			resetFocussedElement(secondLevelMenuEl);
			expressAddClass(secondLevelMenuEl, "u-hide");
			closeAllThirdLevelMenuItemsAndResetChildMenus(secondLevelMenuEl);
		});
	};

	const closeAllThirdLevelMenuItemsAndResetChildMenus = (menuItem: HTMLElement) => {
		const allThirdLevelMenuItems = expressQuerySelectorAll<HTMLElement>(
			menuItem,
			".technical-desktop-second-level-menu-item",
		);

		allThirdLevelMenuItems.forEach((el) => {
			resetFocussedElement(el);
			const contentEl = expressQuerySelector<HTMLElement>(el, ".technical-desktop-second-level-content", false);
			contentEl && expressAddClass(contentEl, "u-hide");
			expressRemoveClass(el, secondLevelActiveClass);
		});
	};

	const closeMenuOnMouseOut = (e: Event) => {
		const menuItem = e.currentTarget as HTMLElement;
		closeFirstLevelMenuItemAndResetChildMenus(menuItem);
		if (e instanceof KeyboardEvent) {
			menuItem.focus();
		}
	};

	const getOrLoadFirstLevelMenuItem = (e: Event) => {
		e.stopPropagation();
		if (e.type.toLowerCase() === "mouseenter" && isTouchDevice()) return;
		const menuItem = e.currentTarget as HTMLElement;
		const secondLevelMenuEl = expressQuerySelector<HTMLElement>(menuItem, ".technical-desktop-menu-second", true);
		const buttonEL = expressQuerySelector<HTMLElement>(menuItem, ".technical-level1-button", true);

		// First hide all other items
		if (e instanceof KeyboardEvent) {
			const allActiveFirstLevelMenuItems = expressQuerySelectorAll<HTMLElement>(
				containerEl,
				`.${firstLevelActiveClass}`,
			);
			allActiveFirstLevelMenuItems.forEach((el) => {
				const menuItem = getSelfOrParentWithClassName<HTMLElement>(el, "technical-desktop-first-level-menu-item");
				menuItem && closeFirstLevelMenuItemAndResetChildMenus(menuItem);
			});
		} else {
			const allSecondLevelMenuItems = expressQuerySelectorAll<HTMLElement>(
				containerEl,
				".technical-desktop-menu-second",
			);
			allSecondLevelMenuItems.forEach((el) => closeAllSecondLevelMenuItemsAndResetChildMenus(el));
		}

		const focusFirstSecondLevelItem = (item: HTMLElement) => {
			if (item) {
				expressAddClass(item, keyboardFocusClass);
				const elToFocus = getElementToFocus(item);
				elToFocus && elToFocus.focus();
			}
		};

		// Set selected item and fetch content if needed
		if (expressQuerySelectorAll(secondLevelMenuEl, ".o-menu-second-level__list.skeleton-loading").length === 1) {
			const itemKey = parseInt(menuItem.getAttribute("data-menu-item") || "", 10);
			const alreadyLoaded = itemKey in loadedMenuItemsKeys;
			if (!alreadyLoaded) {
				loadedMenuItemsKeys[itemKey] = [];
				expressAddClass(buttonEL, firstLevelActiveClass);
				loadDesktopMenuItemAsync(itemKey, -1).then((res) => {
					secondLevelMenuEl.innerHTML = ""; // this removes the skeleton loading
					secondLevelMenuEl.insertAdjacentHTML("beforeend", res);
					window.addEventListener("resize", () => {
						debounce(() => {
							if (!secondLevelMenuEl) return;
							secondLevelMenuEl.style.top =
								Math.floor(expressQuerySelector<HTMLElement>(document, ".technical-header", true).clientHeight) + "px";
						}, 250);
					});
					secondLevelMenuEl.style.top =
						Math.floor(expressQuerySelector<HTMLElement>(document, ".technical-header", true).clientHeight) + "px";
					const buttonEls = expressQuerySelectorAll<HTMLElement>(secondLevelMenuEl, ".technical-third-link-item");
					if (buttonEls.length && buttonEls.every((x) => x.nodeName.toLowerCase() === "a")) {
						const subMenuListEl = expressQuerySelector<HTMLElement>(
							secondLevelMenuEl,
							".o-menu-second-level__list",
							true,
						);
						subMenuListEl.style.left = menuItem.offsetLeft - containerEl.offsetLeft + "px";
					}

					const secondLevelListEl = expressQuerySelector<HTMLElement>(
						secondLevelMenuEl,
						".o-menu-second-level__list",
						true,
					);

					disposable.add(expressEventListener(secondLevelListEl, "keydown", onSecondLevelListKeyDown));

					initSecondLevelListeners(secondLevelMenuEl);

					// To prevent opening the menu, when the visitor already moved to another menu item, we check the active class again
					if (hasClass(buttonEL, firstLevelActiveClass)) {
						expressRemoveClass(secondLevelMenuEl, "u-hide");
						if (e instanceof KeyboardEvent) {
							const secondLevelMenuItems = getSecondLevelMenuEls(secondLevelListEl);
							if (secondLevelMenuItems.length > 0) {
								focusFirstSecondLevelItem(secondLevelMenuItems[0]);
							}
						}
					}
				});
				disposable.add(
					compositeDisposable(
						firstLevelMenuEls.map((el) => expressEventListener(el, "mouseleave", closeMenuOnMouseOut)),
					),
				);
				disposable.add(expressEventListener(secondLevelMenuEl, "click", closeMenu));
			}
		} else {
			if (e.type.toLowerCase() === "click" && isTouchDevice()) {
				if (hasClass(buttonEL, firstLevelActiveClass)) {
					expressRemoveClass(buttonEL, firstLevelActiveClass);
					expressAddClass(secondLevelMenuEl, "u-hide");
					closeAllThirdLevelMenuItemsAndResetChildMenus(secondLevelMenuEl);
				} else {
					expressAddClass(buttonEL, firstLevelActiveClass);
					expressRemoveClass(secondLevelMenuEl, "u-hide");
				}
			} else if (e instanceof KeyboardEvent) {
				expressAddClass(buttonEL, firstLevelActiveClass);
				expressRemoveClass(secondLevelMenuEl, "u-hide");
				const secondLevelListEl = expressQuerySelector<HTMLElement>(
					secondLevelMenuEl,
					".o-menu-second-level__list",
					true,
				);
				const secondLevelMenuItems = getSecondLevelMenuEls(secondLevelListEl);
				if (secondLevelMenuItems.length > 0) {
					expressAddClass(secondLevelMenuItems[0], keyboardFocusClass);
					const secondLevelMenuItemToFocus = expressQuerySelector<HTMLElement>(
						secondLevelMenuItems[0],
						".technical-third-link-item",
						true,
					);
					secondLevelMenuItemToFocus.focus();
				}
			} else {
				expressAddClass(buttonEL, firstLevelActiveClass);
				expressRemoveClass(secondLevelMenuEl, "u-hide");
			}
		}
	};

	const initFirstLevelListeners = () => {
		firstLevelMenuEls.forEach((el) => {
			disposable.add(expressEventListener(el, "mouseenter", getOrLoadFirstLevelMenuItem));
			disposable.add(expressEventListener(el, "click", getOrLoadFirstLevelMenuItem));
			disposable.add(expressEventListener(el, "keydown", onFirstLevelOnKeyDown));
		});
	};

	const getSecondLevelMenuEls = (parent: HTMLElement) => {
		return expressQuerySelectorAll<HTMLElement>(parent, ".technical-desktop-second-level-menu-item");
	};

	const onSecondLevelItemOnKeyDown = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		if (getSelfOrParentWithClassName(currentItem, "technical-desktop-second-level-menu-item")) {
			if (e.key === "ArrowRight") getOrLoadSecondLevelMenuItem(e);
			if (e.key === "Enter") getOrLoadSecondLevelMenuItem(e);
		}
	};

	const getActiveElement = (elements: HTMLElement[]) => {
		let focusedEl: HTMLElement;
		const activeEl = document.activeElement as HTMLElement;
		if (activeEl.nodeName === elements[0].nodeName) {
			focusedEl = activeEl as HTMLElement;
		} else if (activeEl.parentElement.nodeName === elements[0].nodeName) {
			focusedEl = activeEl.parentElement as HTMLElement;
		}
		return focusedEl;
	};

	const onArrowDown = (e: KeyboardEvent, elements: HTMLElement[], activativeClass: string, addActiveClass = true) => {
		e.preventDefault();
		e.stopPropagation();
		if (!elements || elements.length === 0) return;
		let focusedEl = getActiveElement(elements);
		if (!focusedEl) focusedEl = elements.find((el) => hasClass(el, activativeClass));
		let elToFocus = getElementToFocus(elements[0]);
		if (focusedEl) {
			expressRemoveClass(focusedEl, activativeClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(elements, focusedEl);
			const nextEl = elements[indexOfFocusedEl + 1];
			if (nextEl) {
				elToFocus = getElementToFocus(nextEl);
				addActiveClass && expressAddClass(nextEl, activativeClass);
				elToFocus.focus();
			} else {
				addActiveClass && expressAddClass(elements[0], activativeClass);
				elToFocus.focus();
			}
		} else {
			addActiveClass && expressAddClass(elements[0], activativeClass);
			elToFocus.focus();
		}
	};

	const onArrowUp = (e: KeyboardEvent, elements: HTMLElement[], activativeClass: string, addActiveClass = true) => {
		e.preventDefault();
		e.stopPropagation();
		if (!elements || elements.length === 0) return;
		let focusedEl = getActiveElement(elements);
		if (!focusedEl) focusedEl = elements.find((el) => hasClass(el, activativeClass));
		let elToFocus = getElementToFocus(elements[elements.length - 1]);
		if (focusedEl) {
			expressRemoveClass(focusedEl, activativeClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(elements, focusedEl);
			const prevEl = elements[indexOfFocusedEl - 1];
			if (prevEl) {
				elToFocus = getElementToFocus(prevEl);
				addActiveClass && expressAddClass(prevEl, activativeClass);
				elToFocus.focus();
			} else {
				addActiveClass && expressAddClass(elements[elements.length - 1], activativeClass);
				elToFocus.focus();
			}
		} else {
			addActiveClass && expressAddClass(elements[elements.length - 1], activativeClass);
			elToFocus.focus();
		}
	};

	const onFirstLevelOnKeyDown = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		if (getSelfOrParentWithClassName(currentItem, "technical-desktop-first-level-menu-item")) {
			if (e.key === "Escape" || e.key === "ArrowUp") closeMenuOnMouseOut(e);
			if (e.key === "Enter") {
				const firstLevelButtonEl = expressQuerySelector<HTMLElement>(currentItem, ".technical-level1-button", false);
				if (firstLevelButtonEl && hasClass(firstLevelButtonEl, firstLevelActiveClass)) {
					closeMenuOnMouseOut(e);
				} else {
					getOrLoadFirstLevelMenuItem(e);
				}
			}
			if (e.key === "ArrowDown") {
				e.preventDefault();
				getOrLoadFirstLevelMenuItem(e);
			}
		}
	};

	const onSecondLevelListKeyDown = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		const secondLevelMenuItems = getSecondLevelMenuEls(currentItem);

		if (getSelfOrParentWithClassName(currentItem, "o-menu-second-level__list")) {
			if (e.key === "ArrowDown") onArrowDown(e, secondLevelMenuItems, keyboardFocusClass);
			if (e.key === "ArrowUp") onArrowUp(e, secondLevelMenuItems, keyboardFocusClass);
		}
	};

	const onThirdLevelItemKeyDown = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLAnchorElement;
		if (hasClass(currentItem, keyboardActiveClass) && e.key === "Enter") {
			currentItem.click();
			e.stopPropagation();
		}
	};

	const onThirdLevelOnKeyDown = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		const secondLevelContentEl = getSelfOrParentWithClassName<HTMLElement>(
			currentItem,
			"technical-desktop-second-level-content",
		);
		const elementsToFocus = expressQuerySelectorAll<HTMLElement>(secondLevelContentEl, "a");

		elementsToFocus.forEach((el) => {
			disposable.add(expressEventListener(el, "keydown", onThirdLevelItemKeyDown));
			disposable.add(
				expressEventListener(el, "focus", () => {
					const focussedEl = elementsToFocus.find((el) => hasClass(el, keyboardActiveClass));
					focussedEl && expressRemoveClass(focussedEl, keyboardActiveClass);
					expressAddClass(el, keyboardActiveClass);
				}),
			);
		});

		if (secondLevelContentEl) {
			if (e.key === "ArrowDown") onArrowDown(e, elementsToFocus, keyboardActiveClass, false);
			if (e.key === "ArrowUp") onArrowUp(e, elementsToFocus, keyboardActiveClass, false);
			if (e.key === "ArrowRight") e.stopPropagation();
			if (e.key === "ArrowLeft" || e.key === "Escape") {
				e.stopPropagation();
				const parent = currentItem.closest(".technical-desktop-first-level-menu-item") as HTMLElement;
				closeAllThirdLevelMenuItemsAndResetChildMenus(parent);
				const secondLevelContentMenuItem = getSelfOrParentWithClassName<HTMLElement>(
					secondLevelContentEl,
					"technical-desktop-second-level-menu-item",
				);
				if (secondLevelContentMenuItem) {
					const focusedEl = elementsToFocus.find((el) => hasClass(el, keyboardActiveClass));
					if (focusedEl) {
						expressRemoveClass(focusedEl, keyboardActiveClass);
					}
					const secondLevelMenuItemToFocus = expressQuerySelector<HTMLElement>(
						secondLevelContentMenuItem,
						".technical-third-link-item",
						true,
					);
					if (secondLevelMenuItemToFocus) secondLevelMenuItemToFocus.focus();
					expressAddClass(secondLevelContentEl, "u-hide");
				}
			}
		}
	};

	const getOrLoadSecondLevelMenuItem = (e: Event) => {
		e.stopPropagation();
		if (e.type.toLowerCase() === "mouseenter" && isTouchDevice()) return;
		const menuItem = e.currentTarget as HTMLElement;
		if (e instanceof MouseEvent && hasClass(menuItem, secondLevelActiveClass)) return;
		const parent = menuItem.closest(".technical-desktop-first-level-menu-item") as HTMLElement;

		// First hide all other items
		closeAllThirdLevelMenuItemsAndResetChildMenus(parent);

		// Set selected item and fetch content if needed
		expressAddClass(menuItem, secondLevelActiveClass);
		const contentEl = expressQuerySelector<HTMLElement>(menuItem, ".technical-desktop-second-level-content", false);
		if (!contentEl) return;
		expressRemoveClass(contentEl, "u-hide");

		const buttonEl = expressQuerySelector<HTMLElement>(menuItem, ".technical-third-link-item", true);
		if (buttonEl.nodeName.toLowerCase() === "button") {
			const firstLevelIndex = parseInt(parent.getAttribute("data-menu-item") || "", 10);
			const secondLevelIndex = parseInt(menuItem.getAttribute("data-menu-item") || "", 10);
			const alreadyLoaded =
				loadedMenuItemsKeys[firstLevelIndex] && loadedMenuItemsKeys[firstLevelIndex].includes(secondLevelIndex);

			if (!alreadyLoaded) {
				loadedMenuItemsKeys[firstLevelIndex].push(secondLevelIndex);
				loadDesktopMenuItemAsync(firstLevelIndex, secondLevelIndex).then((res) => {
					contentEl.innerHTML = res;
					disposable.add(expressEventListener(contentEl, "keydown", onThirdLevelOnKeyDown));
					if (e instanceof KeyboardEvent) {
						focusFirstThirdLevelItem(contentEl);
					}
				});
			} else {
				if (e instanceof KeyboardEvent) {
					focusFirstThirdLevelItem(contentEl);
				}
			}
		}
	};

	const focusFirstThirdLevelItem = (contentEl: HTMLElement) => {
		const elementsToFocus = expressQuerySelectorAll<HTMLElement>(contentEl, "a");
		if (!elementsToFocus || elementsToFocus.length === 0) return;
		const elToFocus = getElementToFocus(elementsToFocus[0]);
		if (elToFocus) {
			elToFocus.focus();
			expressAddClass(elToFocus, keyboardActiveClass);
		}
	};

	const unLoadSecondLevelMenuItem = (e: MouseEvent) => {
		e.stopPropagation();
		if (e.type.toLowerCase() === "mouseenter" && isTouchDevice()) return;
		const menuItem = e.currentTarget as HTMLElement;
		if (!hasClass(menuItem, secondLevelActiveClass)) return;
		expressRemoveClass(menuItem, secondLevelActiveClass);
	};

	const getElementToFocus = (el: HTMLElement) => {
		if (!el) return null;
		if (el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
			return el;
		} else {
			const childEl = Array.from(el.childNodes).find(
				(el) => el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement || el instanceof HTMLInputElement,
			);
			if (childEl) return childEl as HTMLElement;
			return expressQuerySelector<HTMLElement>(el, "a, button, input") || el;
		}
	};

	const initSecondLevelListeners = (currentMenuItem: HTMLElement) => {
		const secondLevelMenuEls = expressQuerySelectorAll<HTMLElement>(
			currentMenuItem,
			".technical-desktop-second-level-menu-item",
		);

		secondLevelMenuEls.forEach((el: HTMLElement) => {
			disposable.add(expressEventListener(el, "mouseenter", getOrLoadSecondLevelMenuItem));
			disposable.add(expressEventListener(el, "mouseleave", unLoadSecondLevelMenuItem));
			disposable.add(expressEventListener(el, "click", getOrLoadSecondLevelMenuItem));
			disposable.add(expressEventListener(el, "keydown", onSecondLevelItemOnKeyDown));
		});
	};

	// init handlers on load
	initFirstLevelListeners();

	return { dispose: disposable && disposable.dispose };
}
